import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Crosshair, Layers, ShieldCheck, Activity, ChevronRight } from "lucide-react";
import { BRAND, IMG, SERVICES, PROCESS_STEPS, CASE_STUDIES, MECHANISMS } from "@/lib/site";
import SEO, { buildLocalBusinessJsonLd } from "@/components/SEO";

const IMAGE_MAP = IMG;

const Stat = ({ n, label, delay }) => (
  <div className={`reveal reveal-delay-${delay} p-8 border-l border-zinc-200 first:border-l-0`}>
    <div className="font-display text-5xl md:text-6xl font-black leading-none text-zinc-900">{n}</div>
    <div className="tech-label mt-3">{label}</div>
  </div>
);

export default function Home() {
  return (
    <div data-testid="home-page">
      <SEO
        title=""
        description="Specialist UK consultancy diagnosing and repairing reinforced concrete affected by chemical attack (chlorides, carbonation, sulphates, ASR), reinforcement corrosion and fire damage. EN 1504 compliant repair."
        jsonLd={buildLocalBusinessJsonLd()}
      />
      {/* ============ HERO ============ */}
      <section className="relative border-b border-zinc-900 bg-zinc-950">
        <div className="absolute inset-0">
          <img
            src={IMG.rebarCloseup}
            alt="Corroded reinforcement inside broken-out concrete"
            fetchPriority="high"
            className="w-full h-full object-cover opacity-60"
          />
          <div className="hero-overlay" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 pt-24 pb-32 md:pt-32 md:pb-40">
          <div className="flex items-center gap-3 text-[11px] font-mono uppercase tracking-[0.24em] text-[#D94515] reveal">
            <span className="inline-block h-2 w-2 bg-[#D94515]" />
            <span>// SPEC · 01 · CONCRETE DIAGNOSIS &amp; REPAIR</span>
          </div>

          <h1 className="hero-heading text-white text-[46px] leading-[0.95] sm:text-6xl md:text-7xl lg:text-8xl mt-6 max-w-5xl reveal reveal-delay-1">
            We diagnose the <span className="text-[#D94515]">damage</span>.<br />
            Then we rebuild the concrete.
          </h1>

          <p className="mt-8 max-w-2xl text-lg md:text-xl text-zinc-200 leading-relaxed reveal reveal-delay-2">
            {BRAND.name} is a specialist consultancy for reinforced concrete
            suffering chemical attack, reinforcement corrosion, or physical
            damage, from chlorides and carbonation to ASR, sulphates and
            post-fire assessment. We survey, test, break out and reinstate.
          </p>

          <div className="mt-10 flex flex-wrap items-center gap-4 reveal reveal-delay-3">
            <Link to="/contact" className="btn-primary bg-[#D94515] border-[#D94515] hover:bg-white hover:text-zinc-900" data-testid="hero-cta-enquire">
              Request Site Survey <ArrowUpRight className="arrow" size={14} strokeWidth={2.5} />
            </Link>
            <Link to="/services" className="btn-outline on-dark" data-testid="hero-cta-services">
              See Services <ChevronRight size={14} strokeWidth={2.5} />
            </Link>
          </div>

          {/* Coordinate tag */}
        </div>

        {/* Bottom rule */}
        <div className="relative border-t border-zinc-700">
          <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between text-[11px] font-mono uppercase tracking-[0.2em] text-zinc-400">
            <span>[ DIAGNOSTIC · BREAKOUT · TREATMENT · REINSTATEMENT ]</span>
            <span className="hidden md:inline">, UK-WIDE COVERAGE</span>
          </div>
        </div>
      </section>

      {/* ============ STATS STRIP ============ */}
      <section className="border-b border-zinc-200">
        <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-4">
          <Stat n="EN 1504" label="Repair Methodology" delay={1} />
          <Stat n="25mm" label="Min. cover behind bar" delay={2} />
          <Stat n="SA 2½" label="Bar cleaning standard" delay={3} />
          <Stat n="UK" label="Nationwide service" delay={4} />
        </div>
      </section>

      {/* ============ PROBLEM / INSIGHT ============ */}
      <section className="max-w-7xl mx-auto px-6 py-24 md:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="tech-label reveal">// The problem</div>
            <h2 className="font-display text-4xl sm:text-5xl md:text-6xl font-bold uppercase tracking-tight mt-4 leading-[0.95] reveal reveal-delay-1">
              Concrete<br />
              deteriorates.<br />
              Assets fail.
            </h2>
          </div>
          <div className="lg:col-span-7 lg:pl-12 lg:border-l border-zinc-200 space-y-6 text-zinc-700 text-base md:text-lg leading-relaxed reveal reveal-delay-2">
            <p>
              Reinforced concrete is a remarkable material, but it deteriorates.
              Chlorides break the passive film around the steel. Carbonation
              lowers the pH of the cover. Alkali-silica reaction and sulphates
              attack the concrete from within. Fire alters its microstructure
              permanently. Each mechanism looks different, but the outcome is
              the same: cracking, spalling, and a slow loss of structural
              capacity.
            </p>
            <p>
              Left unchecked, spalls fall from soffits, cover to reinforcement
              is lost, and structural capacity quietly erodes. The fix is
              rarely a full replacement, but it does need to be specified and
              executed correctly, on the right mechanism, with the right
              materials.
            </p>
            <p>
              We diagnose the mechanism, quantify the extent, and reinstate the
              element using an EN 1504-compliant methodology. One accountable
              team from first inspection to final finish.
            </p>
            <Link to="/services" className="btn-outline" data-testid="home-cta-see-services">
              Explore our services <ArrowUpRight size={14} strokeWidth={2.5} />
            </Link>
          </div>
        </div>
      </section>

      {/* ============ PROCESS ============ */}
      <section className="bg-zinc-950 text-zinc-100 border-y border-zinc-900">
        <div className="max-w-7xl mx-auto px-6 py-24 md:py-32">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-16">
            <div>
              <div className="tech-label text-zinc-500 reveal">// Method</div>
              <h2 className="font-display text-4xl sm:text-5xl md:text-6xl font-bold uppercase tracking-tight mt-4 leading-[0.95] text-zinc-50 reveal reveal-delay-1">
                A four-stage<br />repair method.
              </h2>
            </div>
            <p className="max-w-md text-zinc-400 text-base reveal reveal-delay-2">
              Every project follows the same discipline, because reinforcement
              corrosion is a chemistry problem, not a cosmetic one.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 border-t border-l border-zinc-800">
            {PROCESS_STEPS.map((s, i) => (
              <div
                key={s.n}
                className={`p-8 border-r border-b border-zinc-800 relative reveal reveal-delay-${i + 1}`}
                data-testid={`process-step-${s.n}`}
              >
                <div className="font-mono text-xs font-bold text-[#D94515] tracking-[0.2em]">STEP · {s.n}</div>
                <div className="font-display text-3xl md:text-4xl font-bold uppercase mt-6 text-zinc-50 leading-none">
                  {s.title}
                </div>
                <div className="rule-line bg-zinc-800 my-6" />
                <p className="text-sm text-zinc-400 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ SERVICES PREVIEW ============ */}
      <section className="max-w-7xl mx-auto px-6 py-24 md:py-32">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-16">
          <div>
            <div className="tech-label reveal">// Capability</div>
            <h2 className="font-display text-4xl sm:text-5xl md:text-6xl font-bold uppercase tracking-tight mt-4 leading-[0.95] reveal reveal-delay-1">
              What we do<br />on site.
            </h2>
          </div>
          <Link to="/services" className="btn-outline self-start md:self-end" data-testid="services-preview-cta">
            All services <ArrowUpRight size={14} strokeWidth={2.5} />
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 border-t border-l border-zinc-200">
          {SERVICES.slice(0, 6).map((s, i) => {
            const Icon = [Crosshair, Activity, Layers, ShieldCheck, Crosshair, Layers][i];
            return (
              <div
                key={s.id}
                className="p-8 border-r border-b border-zinc-200 group hover:bg-zinc-50 transition-colors relative"
                data-testid={`service-card-${s.id}`}
              >
                <div className="flex items-start justify-between">
                  <div className="font-mono text-xs text-zinc-500 tracking-[0.2em]">// {s.code}</div>
                  <Icon size={20} className="text-zinc-400 group-hover:text-[#C2410C] transition-colors" strokeWidth={1.5} />
                </div>
                <div className="font-display text-2xl md:text-3xl font-bold uppercase mt-6 leading-tight">
                  {s.title}
                </div>
                <div className="text-[13px] text-zinc-500 mt-1">{s.tagline}</div>
                <div className="rule-line my-6 bg-zinc-200" />
                <p className="text-sm text-zinc-600 leading-relaxed">{s.body}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* ============ MECHANISMS PREVIEW ============ */}
      <section className="bg-zinc-950 text-zinc-100 border-y border-zinc-900">
        <div className="max-w-7xl mx-auto px-6 py-24 md:py-32">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-16">
            <div>
              <div className="tech-label text-zinc-500 reveal">// Mechanisms</div>
              <h2 className="font-display text-4xl sm:text-5xl md:text-6xl font-bold uppercase tracking-tight mt-4 leading-[0.95] text-zinc-50 reveal reveal-delay-1">
                Every failure has<br />a mechanism.
              </h2>
              <p className="mt-6 max-w-xl text-zinc-400 reveal reveal-delay-2">
                Repair follows diagnosis, and diagnosis follows the mechanism.
                Here are the five most common we see across UK reinforced
                concrete.
              </p>
            </div>
            <Link to="/mechanisms" className="btn-outline on-dark self-start md:self-end" data-testid="mechanisms-preview-cta">
              All mechanisms <ArrowUpRight size={14} strokeWidth={2.5} />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 border-t border-l border-zinc-800">
            {MECHANISMS.map((m) => (
              <Link
                key={m.slug}
                to={`/mechanisms/${m.slug}`}
                className="relative overflow-hidden border-r border-b border-zinc-800 group hover:bg-zinc-900 transition-colors"
                data-testid={`mechanism-preview-${m.slug}`}
              >
                {m.illustration && (
                  <div className="absolute inset-0 pointer-events-none">
                    <img
                      src={m.illustration}
                      alt=""
                      loading="lazy"
                      className="absolute inset-0 w-full h-full object-cover opacity-50 group-hover:opacity-70 transition-opacity duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-b from-zinc-950/55 via-zinc-950/70 to-zinc-950/90" />
                  </div>
                )}
                <div className="relative p-6 md:p-8">
                  <div className="font-mono text-xs font-bold text-[#D94515] tracking-[0.2em]">// {m.code}</div>
                  <div className="font-display text-2xl md:text-3xl font-bold uppercase mt-6 leading-tight text-zinc-50">
                    {m.name}
                  </div>
                  <div className="rule-line bg-zinc-800 my-4" />
                  <p className="text-xs md:text-sm text-zinc-400 leading-relaxed">
                    {m.tagline}
                  </p>
                  <div className="mt-4 text-[11px] font-mono font-bold uppercase tracking-[0.2em] text-zinc-500 group-hover:text-[#D94515] transition-colors flex items-center gap-1.5">
                    Read more <ArrowUpRight size={12} strokeWidth={2.5} />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ============ CASE STUDIES ============ */}
      <section className="bg-zinc-50 border-y border-zinc-200">
        <div className="max-w-7xl mx-auto px-6 py-24 md:py-32">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-16">
            <div>
              <div className="tech-label reveal">// Projects</div>
              <h2 className="font-display text-4xl sm:text-5xl md:text-6xl font-bold uppercase tracking-tight mt-4 leading-[0.95] reveal reveal-delay-1">
                Recent<br />surveys.
              </h2>
            </div>
            <Link to="/case-studies" className="btn-outline self-start md:self-end" data-testid="case-studies-preview-cta">
              All case studies <ArrowUpRight size={14} strokeWidth={2.5} />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-zinc-200">
            {CASE_STUDIES.slice(0, 4).map((cs, i) => (
              <Link
                key={cs.id}
                to="/case-studies"
                className="group relative bg-white overflow-hidden"
                data-testid={`case-study-preview-${cs.id}`}
              >
                <div className="relative aspect-[4/3] overflow-hidden bg-zinc-100">
                  <img
                    src={IMAGE_MAP[cs.image]}
                    alt={cs.title}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-zinc-950/80 via-zinc-950/20 to-transparent" />
                  <div className="absolute top-4 left-4 font-mono text-[11px] uppercase tracking-[0.24em] text-zinc-100 bg-zinc-950/70 px-2 py-1 border border-zinc-100/30">
                    REF · {cs.ref}
                  </div>
                  <div className="absolute bottom-4 left-4 right-4 text-white">
                    <div className="font-mono text-[11px] font-bold uppercase tracking-[0.24em] text-[#D94515]">
                      {cs.tag} · {cs.location}
                    </div>
                    <div className="font-display text-xl md:text-2xl font-bold uppercase mt-2 leading-tight">
                      {cs.title}
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ============ CTA ============ */}
      <section className="max-w-7xl mx-auto px-6 py-24 md:py-32">
        <div className="relative border border-zinc-900 p-10 md:p-16 bg-white">
          <span className="corner-tick tl" />
          <span className="corner-tick tr" />
          <span className="corner-tick bl" />
          <span className="corner-tick br" />

          <div className="tech-label">// Next step</div>
          <h2 className="font-display text-4xl sm:text-5xl md:text-6xl font-bold uppercase tracking-tight mt-4 leading-[0.95] max-w-3xl">
            Concerned about a structure? Let&apos;s take a look.
          </h2>
          <p className="mt-6 max-w-2xl text-zinc-700 text-base md:text-lg">
            Send us the site details and a photograph if you have one. We&apos;ll
            come back with a clear, no-obligation view on next steps.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-4">
            <Link to="/contact" className="btn-primary" data-testid="home-bottom-cta-enquire">
              Start an Enquiry <ArrowUpRight className="arrow" size={14} strokeWidth={2.5} />
            </Link>
            <a href={`mailto:${BRAND.email}`} className="btn-outline" data-testid="home-bottom-cta-email">
              Email us directly <ArrowUpRight size={14} strokeWidth={2.5} />
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
